import { cva, VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { cn } from '@/lib/utils'

const inputContainerVariants = cva('flex items-center rounded group border box-border h-8', {
  variants: {
    variant: {
      default:
        'border border-transparent focus-within:border-doss-focus focus-within:bg-transparent',
      destructive: 'border border-red-7',
      outline: 'border border-gray-4',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const inputVariants = cva(
  `
            group/input flex h-full w-full flex-1 box-border
            cursor-pointer border rounded
            border-transparent bg-background px-3 py-1 text-base
            ring-offset-background file:border-0 file:bg-transparent file:text-sm
            file:font-normal placeholder:text-doss-border
            group-hover:bg-doss-hover focus-visible:outline-none focus-visible:!bg-transparent
            disabled:cursor-not-allowed disabled:opacity-50
            group-focus-within:bg-transparent
            [&::-webkit-calendar-picker-indicator]:hidden
            [&::-webkit-inner-spin-button]:hidden
            [&::-webkit-datetime-edit-month-field]:uppercase
            [&::-webkit-datetime-edit-day-field]:uppercase
            [&::-webkit-datetime-edit-year-field]:uppercase
            [&::-webkit-calendar-picker]:hidden
            [&::-webkit-date-and-time-value]:text-left
            [&::-webkit-inner-spin-button]:-webkit-appearance-none
            [&::-webkit-calendar-picker-indicator]:-webkit-appearance-none
            md:text-xs
          `,
  {
    variants: {
      variant: {
        default: '',
        destructive: '',
        outline: '',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement>, VariantProps<typeof inputVariants> {
  startAdornment?: JSX.Element | null
  endAdornment?: JSX.Element | null
  isBorderless?: boolean
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, endAdornment, startAdornment, type, variant, isBorderless, ...props }, ref) => {
    return (
      <div className={cn(inputContainerVariants({ variant }), isBorderless && 'border-none')}>
        {startAdornment && (
          <div
            className={`box-border flex h-full cursor-pointer items-center border border-transparent bg-background px-3 py-1 text-xs group-focus-within:bg-transparent group-hover:bg-gray-1`}
          >
            {startAdornment}
          </div>
        )}
        <input
          type={type}
          className={cn(
            inputVariants({ variant }),
            type === 'number' && 'hide-spinner',
            type === 'date' && 'pe-1',
            className,
          )}
          ref={ref}
          {...props}
        />
        {endAdornment && (
          <div
            className={`flex h-full cursor-pointer items-center border border-transparent bg-background px-3 py-1 text-xs group-focus-within:bg-transparent group-hover:bg-gray-1`}
          >
            {endAdornment}
          </div>
        )}
      </div>
    )
  },
)
Input.displayName = 'Input'

export { Input }
